import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Tooltip,
  useTheme,
  Divider,
} from "@mui/material";
import {
  Dashboard,
  MenuBook,
  CardMembership,
  Groups,
  DynamicFeed,
  Info,
  ChevronLeft,
  ChevronRight,
  School,
  Star,
  Work,
} from "@mui/icons-material";
import { 
  Backdrop, 
  CircularProgress,
  Typography 
} from "@mui/material";
import { useAuth as useKaaSAuth } from "../../../utils/AuthContext";
import axios from "axios";

const drawerWidth = 260;
const collapsedWidth = 80;

const StudentSidebar = ({ onToggle }) => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const kaasAuth = useKaaSAuth();
  const [open, setOpen] = useState(true);
  const [isSyncing, setIsSyncing] = useState(false);
  const role = localStorage.getItem("role");

  useEffect(() => {
    if (onToggle) onToggle(open);
  }, [open, onToggle]);

  const toggleDrawer = () => {
    setOpen(!open);
  };

  const handleJobHustleClick = async (e) => {
    e.preventDefault();
    setIsSyncing(true);

    // 1. Sync Authentication to GateP
    const token = localStorage.getItem("jwttoken");
    const email = localStorage.getItem("email");
    const kRole = localStorage.getItem("role");

    if (token) {

      // Proactively sync the user to the GateP backend
      try {
        const response = await axios.post("https://tdtlworld.com/dss-gatep-backend/api/sso-sync/", { token });
        
        if (response.status === 200 && response.data.access) {
          const data = response.data;
          
          // Store GateP specific tokens and user data
          localStorage.setItem("access_token", data.access);
          localStorage.setItem("refresh_token", data.refresh);
          
          const gatepUserData = {
            username: data.username,
            email: data.email,
            phone_number: data.phone_number,
            role: data.user_role.toLowerCase(), // Ensure lowercase for ProtectedRoute
          };
          localStorage.setItem("gatep_user", JSON.stringify(gatepUserData));
        }
      } catch (err) {
        console.error("SSO Synchronization failed:", err);
        // Fallback: if sync fails but user might already have tokens, 
        // we could still proceed, but it's safer to alert if tokens are missing.
      }

      // 2. Redirect to GateP based on role mapping
      // Student -> Talent
      // Instructor -> Employer
      if (kRole === "Instructor") {
        navigate("/db-shiksha/gatep/employer");
      } else {
        navigate("/db-shiksha/gatep/talent");
      }
    } else {
      setIsSyncing(false);
      // Fallback if not logged in
      navigate("/db-shiksha/kaas/login");
    }
  };

  const menuItems = [
    { name: "My Dashboard", icon: <Dashboard />, path: "/db-shiksha/kaas/student/dashboardStudent" },
    { name: "All Courses", icon: <MenuBook />, path: "/db-shiksha/kaas/student/coursePage" },
    { name: "My Courses", icon: <School />, path: "/db-shiksha/kaas/student/courses" },
    ...(role === "Student" ? [{ name: "Membership", icon: <CardMembership />, path: "/db-shiksha/kaas/student/membership" }] : []),
    { name: "Job Hustle", icon: <Work />, path: "/db-shiksha/gatep/talent", onClick: handleJobHustleClick },
    { name: "Groups", icon: <Groups />, path: "/db-shiksha/kaas/student/group" },
    { name: "Feeds", icon: <DynamicFeed />, path: "/db-shiksha/kaas/student/feeds" },
    { name: "About Us", icon: <Info />, path: "/db-shiksha/kaas/student/about" },
  ];

  return (
    <Box
      sx={{
        width: open ? drawerWidth : collapsedWidth,
        flexShrink: 0,
        whiteSpace: "nowrap",
        boxSizing: "border-box",
        position: "relative",
        transition: theme.transitions.create("width", {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.enteringScreen,
        }),
      }}
    >
      <Drawer
        variant="permanent"
        open={open}
        sx={{
          "& .MuiDrawer-paper": {
            width: open ? drawerWidth : collapsedWidth,
            transition: theme.transitions.create("width", {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
            overflowX: "hidden",
            bgcolor: "background.paper",
            borderRight: "1px solid",
            borderColor: "divider",
            height: "calc(100vh - 64px)", // Assuming Header is 64px
            top: "64px",
            zIndex: 10,
          },
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "flex-end", p: 1 }}>
          <IconButton onClick={toggleDrawer}>
            {open ? <ChevronLeft /> : <ChevronRight />}
          </IconButton>
        </Box>
        <Divider />
        <List sx={{ px: 1.5, py: 2 }}>
          {menuItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <ListItem key={item.name} disablePadding sx={{ display: "block", mb: 0.5 }}>
                <Tooltip title={!open ? item.name : ""} placement="right">
                  <ListItemButton
                    component={item.onClick ? "div" : Link}
                    to={item.onClick ? undefined : item.path}
                    onClick={item.onClick}
                    sx={{
                      minHeight: 48,
                      justifyContent: open ? "initial" : "center",
                      px: 2.5,
                      borderRadius: "12px",
                      bgcolor: isActive ? "rgba(212, 175, 55, 0.1)" : "transparent",
                      color: isActive ? "#D4AF37" : "text.primary",
                      "&:hover": {
                        bgcolor: isActive ? "rgba(212, 175, 55, 0.15)" : "rgba(0, 0, 0, 0.04)",
                        "& .MuiListItemIcon-root": { color: "#D4AF37" },
                      },
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        mr: open ? 3 : "auto",
                        justifyContent: "center",
                        color: isActive ? "#D4AF37" : "inherit",
                        transition: "color 0.2s ease",
                      }}
                    >
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.name}
                      sx={{
                        opacity: open ? 1 : 0,
                        "& .MuiTypography-root": {
                          fontWeight: isActive ? 700 : 500,
                          fontSize: "0.95rem",
                        },
                      }}
                    />
                    {isActive && open && (
                      <Box
                        sx={{
                          width: 4,
                          height: 20,
                          bgcolor: "#D4AF37",
                          borderRadius: 2,
                          ml: 1,
                        }}
                      />
                    )}
                  </ListItemButton>
                </Tooltip>
              </ListItem>
            );
          })}
        </List>
      </Drawer>

      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 101, display: 'flex', flexDirection: 'column', gap: 2 }}
        open={isSyncing}
      >
        <CircularProgress color="inherit" />
        <Typography variant="h6" sx={{ fontWeight: 500 }}>
          Syncing with Job Hustle...
        </Typography>
      </Backdrop>
    </Box>
  );
};

export default StudentSidebar;
